import React, { useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MainLayout from "../../../../layouts/MainLayout";
import "../../../../assets/styles/custom-styles.css";
import Form from "../../../../components/forms/Form";
import { eventJobFields } from "../../../../constants/forms/eventJobFields";
import useLoader from "../../../../hooks/useLoader";
import useForm from "../../../../hooks/useForm";
import {
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../../utils/objectUtils";
import useBaseService from "../../../../hooks/services/useBaseService";
import { entities } from "../../../../constants/entities";
import PageHeader from "../../../../components/PageHeader";
import SectionHeader from "../../../../components/forms/SectionHeader";
import SimpleBody from "../../../../components/forms/SimpleBody";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useNotification from "../../../../hooks/useNotification";
import useQueryWithLoader from "../../../../hooks/useQueryWithLoader";

const EditEventJobContractPage = () => {
  const navigate = useNavigate();
  const { contractId, eventId, additiveId, jobId } = useParams();
  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const queryClient = useQueryClient();

  const {
    getByColumn: baseServiceGetByColumn,
    put: baseServicePut,
    setFormErrors,
  } = useBaseService(navigate);

  const { formData, formatData, handleChange, formErrors, resetForm } = useForm(
    setDefaultFieldValues(eventJobFields),
  );

  const {
    data: eventJobData,
    isLoading,
    isError,
    error,
  } = useQueryWithLoader({
    queryKey: ["eventJob", contractId, eventId, additiveId, jobId],
    queryFn: async () => {
      const response = await baseServiceGetByColumn(
        entities.contracts.eventAdditiveJobUpdate(
          contractId,
          eventId,
          additiveId,
          jobId,
        ),
      );
      return response.result;
    },
    enabled: !!contractId && !!eventId && !!additiveId && !!jobId,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!isLoading && !isError && eventJobData) {
      formatData(eventJobData, eventJobFields);
    }
    if (isError && error) {
      showNotification(
        "error",
        `Erro ao carregar serviço do evento: ${
          error.message || error.response?.data?.message || "Erro desconhecido."
        }`,
      );
      navigate(`/aluguel/contratos/${contractId}/eventos/${eventId}/detalhes/`);
    }
  }, [
    isLoading,
    isError,
    error,
    eventJobData,
    formatData,
    navigate,
    contractId,
    eventId,
    showNotification,
  ]);

  const updateEventJobMutation = useMutation({
    mutationFn: async () => {
      return await baseServicePut(
        entities.contracts.eventAdditiveJobUpdate(
          contractId,
          eventId,
          additiveId,
          jobId,
        ),
        formData,
      );
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        resetForm();
        queryClient.invalidateQueries({
          queryKey: ["eventJob", contractId, eventId, additiveId, jobId],
        });
        queryClient.invalidateQueries({
          queryKey: ["eventJobs", contractId, eventId],
        });
        queryClient.invalidateQueries({
          queryKey: ["contractEventJobs", contractId, eventId],
        });
        handleBack();
      }
    },
    onError: (err) => {
      console.error("onError ", err);
      if (err.response?.data?.errors) {
        setFormErrors(err.response.data.errors);
      }
      hideLoader();
    },
  });

  useEffect(() => {
    if (isLoading || updateEventJobMutation.isPending) {
      showLoader();
    } else {
      hideLoader();
    }
  }, [isLoading, updateEventJobMutation.isPending, showLoader, hideLoader]);

  const handleSubmit = useCallback(async () => {
    try {
      await updateEventJobMutation.mutateAsync();
    } catch (error) {
      console.error("Erro ao enviar serviço do evento:", {
        status: error.status,
        message: error.message,
      });
    }
  }, [updateEventJobMutation]);

  const handleBack = () => {
    navigate(`/aluguel/contratos/${contractId}/eventos/${eventId}/detalhes/`);
  };

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Editar Serviço do Evento"
        showBackButton={true}
        backUrl={`/aluguel/contratos/${contractId}/eventos/${eventId}/detalhes/`}
      />

      <div className="container-fluid p-1">
        <Form
          onSubmit={handleSubmit}
          initialFormData={formData}
          textSubmit="Editar"
          textLoadingSubmit="Editando..."
          handleBack={handleBack}
        >
          {() => {
            const renderedSections = new Set();

            return eventJobFields.map((section, index) => {
              const sectionType = section.section.split(" - ")[0];
              const showHeader = !renderedSections.has(sectionType);

              if (showHeader) {
                renderedSections.add(sectionType);
              }

              return (
                <div key={section.section + " " + index}>
                  {showHeader && (
                    <SectionHeader
                      section={section.section}
                      viewTable={false}
                      setViewTable={() => {}}
                      addFieldsInData={() => {}}
                    />
                  )}

                  <SimpleBody
                    fields={section.fields}
                    formErrors={normalizeFormErrors(formErrors)}
                    formData={formData}
                    handleFieldChange={handleChange}
                    getOptions={() => {}}
                    getSelectedValue={() => {}}
                  />
                </div>
              );
            });
          }}
        </Form>
      </div>
    </MainLayout>
  );
};

export default EditEventJobContractPage;
